import { useId } from "react";

export type Vertex = [number, number];

const FLOOR_PLATE = { cx: 320, cy: 132, hw: 318, hh: 131 } as const;

export function project([u, v]: Vertex, theta: number): Vertex {
  const du = u - 0.5;
  const dv = v - 0.5;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const ru = 0.5 + du * cos - dv * sin;
  const rv = 0.5 + du * sin + dv * cos;
  return [
    FLOOR_PLATE.cx - FLOOR_PLATE.hw + FLOOR_PLATE.hw * (ru + rv),
    FLOOR_PLATE.cy - FLOOR_PLATE.hh * ru + FLOOR_PLATE.hh * rv,
  ];
}

export function buildIsometricBlock(base: Vertex[], height: number) {
  const topFace = base.map(([x, y]): Vertex => [x, y - height]);
  const walls = base
    .map((point, i) => {
      const nextIndex = (i + 1) % base.length;
      const next = base[nextIndex];
      const dx = next[0] - point[0];
      const dy = next[1] - point[1];
      const length = Math.hypot(dx, dy);
      return {
        id: i,
        points: [point, next, topFace[nextIndex], topFace[i]],
        depthY: (point[1] + next[1]) / 2,
        rightness: length > 0 ? dy / length : 0,
      };
    })
    .sort((a, b) => a.depthY - b.depthY);
  const depthY = base.reduce((sum, [, y]) => sum + y, 0) / base.length;
  return { walls, topFace, depthY };
}

export function toPoints(vertices: Vertex[]) {
  return vertices.map(([x, y]) => `${x},${y}`).join(" ");
}

/** A useId() value that is safe inside SVG `url(#…)` references. */
export function useSvgId(prefix: string) {
  return `${prefix}${useId().replace(/[^\w-]/g, "")}`;
}
